import type { ChangeEvent, JSX } from 'react';
import type { CategoryDef, DashboardPayload } from '../dashboard-types.js';

interface DimensionFilterProps {
  dimensions: DashboardPayload['dimensions'];
  categories: CategoryDef[];
  catDim: DashboardPayload['catDim'];
  ranges: Record<string, [number, number]>;
  onChange: (next: Record<string, [number, number]>) => void;
}

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business item_skus.{full_length, dress_length, ...} の値レンジで型数集計を絞り込む寸法フィルターパネル（折りたたみ可能）
 * @graph-connects none
 */
export function DimensionFilter(props: DimensionFilterProps): JSX.Element {
  const activeCount = props.dimensions.filter((d) => {
    const r = props.ranges[d.key];
    return !!r && (r[0] !== d.min || r[1] !== d.max);
  }).length;

  /** @graph-connects none */
  function setSide(key: string, side: 'min' | 'max', value: number): void {
    const dim = props.dimensions.find((d) => d.key === key);
    if (!dim) return;
    const v = Math.max(dim.min, Math.min(dim.max, value));
    const cur = props.ranges[key] ?? [dim.min, dim.max];
    const next: [number, number] =
      side === 'min' ? [Math.min(v, cur[1]), cur[1]] : [cur[0], Math.max(v, cur[0])];
    props.onChange({ ...props.ranges, [key]: next });
  }

  /** @graph-connects none */
  function reset(key: string): void {
    const dim = props.dimensions.find((d) => d.key === key);
    if (!dim) return;
    props.onChange({ ...props.ranges, [key]: [dim.min, dim.max] });
  }

  /** @graph-connects none */
  function resetAll(): void {
    const next: Record<string, [number, number]> = {};
    for (const d of props.dimensions) next[d.key] = [d.min, d.max];
    props.onChange(next);
  }

  return (
    <details className="dim-filter">
      <summary className="dim-filter-summary">
        <svg
          width={14}
          height={14}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
        >
          <path d="M2 4h12M4 8h8M6 12h4" />
        </svg>
        <span>寸法フィルター</span>
        <span style={{ fontSize: 11, color: 'var(--text-subtle)', fontWeight: 400 }}>
          item_skus
          の各カラム値レンジで型数の集計対象を絞り込みます（現在のスナップショット・時系列履歴の両方に適用）
        </span>
        <span className={`active-count ${activeCount > 0 ? 'has-active' : ''}`}>
          {activeCount === 0 ? (
            '未設定（すべての寸法レンジが既定値）'
          ) : (
            <>
              <span className="badge">{activeCount}</span> 寸法フィルター適用中
            </>
          )}
        </span>
        <span className="chev"></span>
      </summary>
      <div className="dim-filter-body">
        <div className="dim-grid">
          {props.dimensions.map((d) => {
            const r = props.ranges[d.key] ?? [d.min, d.max];
            const span = d.max - d.min;
            const left = ((r[0] - d.min) / span) * 100;
            const width = ((r[1] - r[0]) / span) * 100;
            const isActive = r[0] !== d.min || r[1] !== d.max;
            const applicableCats = props.categories
              .filter((c) => props.catDim[c.id]?.[d.key])
              .map((c) => c.name);
            const handleMin = (e: ChangeEvent<HTMLInputElement>): void => {
              setSide(d.key, 'min', Number(e.target.value));
            };
            const handleMax = (e: ChangeEvent<HTMLInputElement>): void => {
              setSide(d.key, 'max', Number(e.target.value));
            };
            const handleReset = (): void => {
              reset(d.key);
            };
            return (
              <div key={d.key} className={`dim-item ${isActive ? 'active' : ''}`}>
                <div className="dim-label">
                  <span>
                    {d.label}
                    <span className="field-name">item_skus.{d.key}</span>
                  </span>
                  <button type="button" className="reset-link" onClick={handleReset}>
                    リセット
                  </button>
                </div>
                <div className="dim-inputs">
                  <input
                    type="number"
                    min={d.min}
                    max={d.max}
                    step={d.step}
                    value={r[0]}
                    onChange={handleMin}
                  />
                  <span>〜</span>
                  <input
                    type="number"
                    min={d.min}
                    max={d.max}
                    step={d.step}
                    value={r[1]}
                    onChange={handleMax}
                  />
                  <span>{d.unit}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-subtle)' }}>
                    既定: {d.min}–{d.max}
                  </span>
                </div>
                <div className="dim-track">
                  <div
                    className="dim-track-fill"
                    style={{ left: `${String(left)}%`, width: `${String(width)}%` }}
                  ></div>
                </div>
                <div className="dim-applicable">
                  <b>対象カテゴリ:</b>{' '}
                  {applicableCats.length === 0 ? (
                    <i style={{ color: 'var(--danger)' }}>該当カテゴリなし</i>
                  ) : (
                    applicableCats.join(' / ')
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="dim-actions">
          <span>
            ※ 各カテゴリで該当しないカラム（例: スカートに
            sleeve_length）は自動で除外され、フィルターは適用されません。
          </span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={resetAll}>
            全寸法フィルターをリセット
          </button>
        </div>
      </div>
    </details>
  );
}
